import React from 'react';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
} from '@mui/material';

import { FINANCE_CURRENCIES } from '~/consts/currencies';

import {
  getMissingRequiredPackageFields,
  getPackageBaseCurrency,
  getPackageCampaignCode,
  getPackageDisplayNameValue,
  getPackageDisplayPrice,
  getPackageNumberOfNights,
  getPackageValue,
} from '../../utils';

import PackageIdentifier from './PackageIdentifier';
import PackageNumberField from './PackageNumberField';
import PackageTextField from './PackageTextField';

interface Props {
  availabilityRatesUrl?: string;
  isAutofillDisabled?: boolean;
  isAutofilling?: boolean;
  isCleanDisplayNameDisabled?: boolean;
  isCleaningDisplayName?: boolean;
  offerPackage: App.Package;
  originalPackageName?: string;
  onChange: (nextPackageValues: Partial<App.Package> & Record<string, unknown>) => void;
  onAutofill: () => void;
  onCleanDisplayName: () => void;
}

export default function DetailsTab({
  availabilityRatesUrl,
  isAutofillDisabled,
  isAutofilling,
  isCleanDisplayNameDisabled,
  isCleaningDisplayName,
  offerPackage,
  originalPackageName,
  onAutofill,
  onChange,
  onCleanDisplayName,
}: Props) {
  const sourcePackageUuid = offerPackage.le_package_id;
  const sourceDealOptionSalesforceId = offerPackage.id_salesforce_external
    ? String(offerPackage.id_salesforce_external)
    : undefined;
  const baseCurrency = getPackageBaseCurrency(offerPackage);
  const currencyOptions = Array.from(new Set([baseCurrency, ...FINANCE_CURRENCIES]));
  const missingRequiredFieldKeys = new Set(getMissingRequiredPackageFields(offerPackage).map((field) => field.key));
  const isDisplayNameMissing = missingRequiredFieldKeys.has('name');
  const isPriceMissing = missingRequiredFieldKeys.has('price');
  const isNumberOfNightsMissing = missingRequiredFieldKeys.has('numberOfNights');

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          spacing={2}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2.5}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <PackageIdentifier label="Package UUID" value={sourcePackageUuid} />
            <PackageIdentifier label="Deal option SF ID" value={sourceDealOptionSalesforceId} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              size="small"
              variant="outlined"
              href={availabilityRatesUrl}
              target="_blank"
              rel="noreferrer"
              disabled={!availabilityRatesUrl}
            >
              Availability & rates
            </Button>
            <Button size="small" startIcon={<AutoFixHighIcon />} disabled={isAutofillDisabled} onClick={onAutofill}>
              {isAutofilling ? 'Matching with AI...' : 'Autofill from deal option'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 3,
        }}
      >
        <Box sx={{ gridColumn: '1 / -1' }}>
          <PackageTextField label="Deal option name" value={originalPackageName || ''} disabled />
        </Box>
        <Stack spacing={1}>
          <PackageTextField
            label="Display name"
            required
            value={getPackageDisplayNameValue(offerPackage)}
            error={isDisplayNameMissing}
            helperText={isDisplayNameMissing ? 'Display name is required' : undefined}
            onChange={(value) => onChange({ name: value })}
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              variant="text"
              startIcon={<AutoFixHighIcon />}
              disabled={isCleanDisplayNameDisabled || !originalPackageName}
              onClick={onCleanDisplayName}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
            >
              {isCleaningDisplayName ? 'Rewriting...' : 'Rewrite with AI'}
            </Button>
          </Stack>
        </Stack>
        <PackageTextField
          label="Campaign code"
          value={getPackageCampaignCode(offerPackage)}
          onChange={(value) => onChange({ campaign_code: value })}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 3,
        }}
      >
        <Stack spacing={1}>
          <FormLabel>Base currency</FormLabel>
          <FormControl size="small" fullWidth>
            <Select value={baseCurrency} disabled>
              {currencyOptions.map((currency) => (
                <MenuItem key={currency} value={currency}>
                  {currency}
                </MenuItem>
              ))}
            </Select>
            {!baseCurrency && <FormHelperText error>Base currency is required</FormHelperText>}
          </FormControl>
        </Stack>
        <PackageNumberField
          label="Price"
          value={getPackageDisplayPrice(offerPackage)}
          error={isPriceMissing}
          helperText={isPriceMissing ? 'Price must be greater than 0' : undefined}
        />
        <PackageNumberField label="Value" value={getPackageValue(offerPackage)} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 3,
        }}
      >
        <PackageNumberField
          label="Number of nights"
          value={getPackageNumberOfNights(offerPackage)}
          error={isNumberOfNightsMissing}
          helperText={isNumberOfNightsMissing ? 'Number of nights must be greater than 0' : undefined}
        />
      </Box>
    </Stack>
  );
}
